import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { PAYMENT_METHODS, label } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, AlertTriangle, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export function RecordPaymentDialog({ open, onOpenChange, defaultClient, onSaved }) {
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const empty = { client_id: defaultClient || "", amount: "", payment_date: "", method: "bank_transfer", reference: "", notes: "" };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  React.useEffect(() => { setF((p) => ({ ...empty, client_id: defaultClient || p.client_id })); }, [defaultClient, open]);

  const save = async () => {
    if (!f.client_id) return toast.error("Select a client");
    if (!f.amount || Number(f.amount) <= 0) return toast.error("Enter a valid amount");
    setSaving(true);
    try {
      await api.post("/payments", { client_id: f.client_id, amount: Number(f.amount), payment_date: f.payment_date || null, method: f.method, reference: f.reference || null, notes: f.notes || null });
      toast.success("Payment recorded & allocated");
      onOpenChange(false);
      onSaved && onSaved();
      setF(empty);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Record Payment</DialogTitle></DialogHeader>
        <p className="-mt-1 text-xs text-zinc-500">Amount is auto-allocated to the oldest outstanding periods first. Any surplus becomes advance credit.</p>
        <div className="grid grid-cols-1 gap-3 py-2 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="text-xs text-zinc-500">Client *</Label>
            <Select value={f.client_id} onValueChange={(v) => set("client_id", v)} disabled={!!defaultClient}>
              <SelectTrigger className="mt-1" data-testid="payment-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-zinc-500">Amount Received (₹) *</Label><Input type="number" value={f.amount} onChange={(e) => set("amount", e.target.value)} className="mt-1" data-testid="payment-amount-input" placeholder="0" /></div>
          <div>
            <Label className="text-xs text-zinc-500">Method</Label>
            <Select value={f.method} onValueChange={(v) => set("method", v)}>
              <SelectTrigger className="mt-1" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-zinc-500">Payment Date</Label><Input type="date" value={f.payment_date} onChange={(e) => set("payment_date", e.target.value)} className="mt-1" /></div>
          <div><Label className="text-xs text-zinc-500">Reference / Txn No.</Label><Input value={f.reference} onChange={(e) => set("reference", e.target.value)} className="mt-1" /></div>
          <div className="sm:col-span-2"><Label className="text-xs text-zinc-500">Notes</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="payment-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Payment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoneyCard({ label: l, value, tone }) {
  const map = { green: "text-emerald-600", orange: "text-amber-600", red: "text-red-600", blue: "text-blue-600", default: "text-zinc-900" };
  return (
    <Card className="rounded-lg border-zinc-200 p-5 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">{l}</div>
      <div className={`metric-value mt-2 text-2xl font-semibold ${map[tone] || map.default}`}>{inr(value)}</div>
    </Card>
  );
}

export default function Payments() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const { data: summary, isLoading } = useQuery({ queryKey: ["payments-summary"], queryFn: async () => (await api.get("/payments/summary")).data });
  const { data: receipts = [] } = useQuery({ queryKey: ["payments"], queryFn: async () => (await api.get("/payments")).data });

  const refresh = () => {
    ["payments-summary", "payments", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  if (isLoading || !summary)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Payments & Billing</h1>
          <p className="mt-1 text-sm text-zinc-500">Billing ledger, receipts & auto-calculated outstanding</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="record-payment-button"><Plus className="mr-2 h-4 w-4" />Record Payment</Button>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <MoneyCard label="Received (Month)" value={summary.received_this_month} tone="green" />
        <MoneyCard label="Due Today" value={summary.due_today} tone="orange" />
        <MoneyCard label="Due This Week" value={summary.due_this_week} tone="orange" />
        <MoneyCard label="Overdue" value={summary.overdue} tone="red" />
        <MoneyCard label="Total Outstanding" value={summary.total_outstanding} />
        <MoneyCard label="Advance / Credit" value={summary.advance_credit} tone="blue" />
      </div>

      {/* Overdue clients */}
      <Card className="overflow-hidden rounded-lg border-zinc-200 shadow-sm">
        <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-4">
          <AlertTriangle className="h-4 w-4 text-red-500" /><h3 className="font-heading text-base font-semibold">Overdue Clients</h3>
          <Badge color="red">{summary.overdue_clients.length}</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-zinc-100 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-4 py-3 text-left font-medium">Client</th>
              <th className="px-4 py-3 text-right font-medium">Outstanding</th>
              <th className="px-4 py-3 text-center font-medium">Overdue Periods</th>
              <th className="px-4 py-3 text-left font-medium">Oldest Due</th>
              <th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {summary.overdue_clients.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-zinc-400">No overdue clients 🎉</td></tr>}
              {summary.overdue_clients.map((c) => (
                <tr key={c.client_id} data-testid={`overdue-row-${c.client_id}`} className="cursor-pointer border-b border-zinc-50 hover:bg-zinc-50" onClick={() => navigate(`/clients/${c.client_id}?tab=billing`)}>
                  <td className="px-4 py-3 font-medium text-zinc-900">{c.client_name}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-red-600">{inr(c.outstanding)}</td>
                  <td className="px-4 py-3 text-center">{c.overdue_periods}</td>
                  <td className="px-4 py-3 text-red-600">{fmtDate(c.oldest_due)}</td>
                  <td className="px-4 py-3 text-right"><ChevronRight className="h-4 w-4 text-zinc-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Recent receipts */}
      <Card className="overflow-hidden rounded-lg border-zinc-200 shadow-sm">
        <div className="border-b border-zinc-100 px-5 py-4"><h3 className="font-heading text-base font-semibold">Recent Payments</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-zinc-100 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-4 py-3 text-left font-medium">Client</th>
              <th className="px-4 py-3 text-left font-medium">Date</th>
              <th className="px-4 py-3 text-right font-medium">Amount</th>
              <th className="px-4 py-3 text-left font-medium">Method</th>
              <th className="px-4 py-3 text-left font-medium">Reference</th>
              <th className="px-4 py-3 text-right font-medium">Unallocated</th>
            </tr></thead>
            <tbody>
              {receipts.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-zinc-400">No payments recorded yet</td></tr>}
              {receipts.map((p) => (
                <tr key={p.id} data-testid={`payment-row-${p.id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                  <td className="px-4 py-3 font-medium text-zinc-900">{p.client_name}{p.migrated && <span className="ml-2 text-[10px] uppercase text-zinc-400">imported</span>}</td>
                  <td className="px-4 py-3 text-zinc-600">{fmtDate(p.payment_date)}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-600">{inr(p.amount)}</td>
                  <td className="px-4 py-3 text-zinc-600">{PAYMENT_METHODS.find((m) => m.v === p.method)?.l || p.method}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{p.reference || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-blue-600">{p.credit > 0 ? inr(p.credit) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <RecordPaymentDialog open={addOpen} onOpenChange={setAddOpen} onSaved={refresh} />
    </div>
  );
}
